import { useState, useEffect } from "react";
import { appStoreCatalog, macOSCatalog } from "@/data/mockApps";
import { Application } from "@/types/application";
import { toast } from "sonner";
import * as appStoreApi from "@/services/appStoreApi";
import * as privateAppsApi from "@/services/privateAppsApi";
import type { AppStoreApplication, ApplicationDTO } from "@/types/api";

/** Map the backend Application entity to the local Application type */
const mapApiToApplication = (app: AppStoreApplication | ApplicationDTO): Application => ({
  id: ('id' in app ? app.id : (app as ApplicationDTO).uuid) || crypto.randomUUID(),
  name: app.name,
  icon: ('iconUrl' in app ? app.iconUrl : undefined) || "",
  description: ('description' in app ? (app as AppStoreApplication).description : undefined) || "",
  version: app.version || "",
  size: app.size || "",
  releaseDate: ('createdAt' in app ? (app as AppStoreApplication).createdAt : undefined) || "",
  source: (('applicationType' in app ? (app as AppStoreApplication).applicationType : app.type) === "private"
    ? "private"
    : "public") as Application["source"],
  platform: (app.platform || "iOS") as Application["platform"],
  bundleId: app.bundleId || "",
  categories: [],
  customers: [],
  status: "active" as Application["status"],
  appStoreId: app.trackId || undefined,
  minimumOsVersion: app.minimumOsVersion || undefined,
  developer: ('developer' in app ? (app as any).developer : undefined),
  rating: ('rating' in app ? (app as any).rating : undefined),
});

export const useApplications = (customerId?: string) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      // Fetch private apps from the backend
      const response = await privateAppsApi.listPrivateApps();
      const privateData = response.data || [];
      const mapped = privateData.map(mapApiToApplication);
      setApplications(mapped);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch applications"));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [customerId]);

  const addApplication = async (app: Omit<Application, "id">): Promise<Application> => {
    try {
      const saved = await appStoreApi.savePublicApp({
        name: app.name,
        bundleId: app.bundleId || undefined,
        platform: app.platform as "iOS" | "macOS",
        version: app.version,
        size: app.size,
        description: app.description,
        trackId: app.appStoreId || undefined,
        iconUrl: app.icon,
        minimumOsVersion: app.minimumOsVersion,
      });
      const newApp = mapApiToApplication(saved);
      setApplications((prev) => [newApp, ...prev]);
      toast.success("Application added successfully");
      return newApp;
    } catch (error) {
      console.error("Failed to add application:", error);
      toast.error("Failed to add application");
      throw error;
    }
  };

  const updateApplication = async (updatedApp: Application): Promise<Application> => {
    // Save as public app (upsert)
    try {
      const saved = await appStoreApi.savePublicApp({
        id: updatedApp.id,
        name: updatedApp.name,
        bundleId: updatedApp.bundleId || undefined,
        platform: updatedApp.platform as "iOS" | "macOS",
        version: updatedApp.version,
        size: updatedApp.size,
        description: updatedApp.description,
        trackId: updatedApp.appStoreId || undefined,
        iconUrl: updatedApp.icon,
        minimumOsVersion: updatedApp.minimumOsVersion,
      });
      const mapped = mapApiToApplication(saved);
      setApplications((prev) => prev.map((a) => (a.id === updatedApp.id ? mapped : a)));
      toast.success("Application updated successfully");
      return mapped;
    } catch (error) {
      console.error("Failed to update application:", error);
      toast.error("Failed to update application");
      throw error;
    }
  };

  const removeApplication = async (appId: string): Promise<void> => {
    try {
      await appStoreApi.batchDeleteApps([appId]);
      setApplications((prev) => prev.filter((app) => app.id !== appId));
      toast.success("Application removed successfully");
    } catch (error) {
      console.error("Failed to remove application:", error);
      toast.error("Failed to remove application");
      throw error;
    }
  };

  const getAppStoreCatalog = () => appStoreCatalog;
  const getMacOSCatalog = () => macOSCatalog;

  return {
    applications,
    loading,
    error,
    addApplication,
    updateApplication,
    removeApplication,
    getAppStoreCatalog,
    getMacOSCatalog,
    refetch: fetchApplications,
  };
};
